import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import useDataStore from "../../store/dataStore";

const BASE_URL = "https://mis.empowerplus.in";

const emptyForm = {
    beneficiaryId: "",
    sector: "",
    setupType: "",
    udyamRegistration: "",
    udyamRegistrationNumber: "",
    logbooksCreated: "",
    digitalPaymentsSetup: "",
    brandingProvided: "",
    gstRegistered: "",
    gstNumber: "",
    platform1Listing: "",
    platform1Link: "",
    platform2Listing: "",
    platform2Link: "",
    platform3Listing: "",
    platform3Link: "",
    platform4Listing: "",
    platform4Link: "",
    platform5Listing: "",
    platform5Link: "",
    trade: "",
    businessName: "",
};

const COLUMNS = [
    { key: "beneficiaryId",          label: "Beneficiary ID" },
    { key: "sector",                 label: "Sector" },
    { key: "setupType",              label: "Setup Type" },
    { key: "udyamRegistration",      label: "Udyam Registration" },
    { key: "udyamRegistrationNumber",label: "Udyam Reg. No." },
    { key: "logbooksCreated",        label: "Logbooks Created" },
    { key: "digitalPaymentsSetup",   label: "Digital Payments" },
    { key: "brandingProvided",       label: "Branding Provided" },
    { key: "gstRegistered",          label: "GST Registered" },
    { key: "gstNumber",              label: "GST Number" },
    { key: "platform1Listing",       label: "Platform 1" },
    { key: "platform1Link",          label: "Platform 1 Link" },
    { key: "platform2Listing",       label: "Platform 2" },
    { key: "platform2Link",          label: "Platform 2 Link" },
    { key: "platform3Listing",       label: "Platform 3" },
    { key: "platform3Link",          label: "Platform 3 Link" },
    { key: "platform4Listing",       label: "Platform 4" },
    { key: "platform4Link",          label: "Platform 4 Link" },
    { key: "platform5Listing",       label: "Platform 5" },
    { key: "platform5Link",          label: "Platform 5 Link" },
    { key: "trade",                  label: "Trade" },
    { key: "businessName",           label: "Business Name" },
];

// Excel template header order must match backend column indices 0–19
const TEMPLATE_HEADERS = [
    "Beneficiary ID", "Sector", "Setup Type", "Udyam Registration",
    "Udyam Registration Number", "Logbooks Created", "Digital Payments Setup",
    "Branding Provided", "GST (Business Registered)", "GST Number",
    "Platform 1 Listing", "Platform 1 Link",
    "Platform 2 Listing", "Platform 2 Link",
    "Platform 3 Listing", "Platform 3 Link",
    "Platform 4 Listing", "Platform 4 Link",
    "Platform 5 Listing", "Platform 5 Link",
    "Trade", "Business Name",
];

export default function EDPBusinessSetup() {
    const token = localStorage.getItem("token");
    const store = useDataStore();
    const CACHE_KEY = "enterpriseSupport";

    const [msg, setMsg] = useState({ type: "", text: "" });
    const [rows, setRows] = useState(() => {
        const cached = store.getRows(CACHE_KEY);
        return cached.length > 0 ? cached : [];
    });
    const [loading, setLoading] = useState(false);
    const [allLoaded, setAllLoaded] = useState(() => store.isAllLoaded(CACHE_KEY));
    const [loadingAll, setLoadingAll] = useState(false);
    const [search, setSearch] = useState("");
    const [visibleCount, setVisibleCount] = useState(100);
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    const flash = (type, text) => setMsg({ type, text });

    const authHeaders = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token]
    );
    const jsonHeaders = useMemo(
        () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }),
        [token]
    );

    const safeJson = async (res) => {
        const text = await res.text();
        try { return text ? JSON.parse(text) : null; }
        catch { return text; }
    };

    /* ================= FETCH ================= */
    const fetchRows = async () => {
        const cached = store.getRows(CACHE_KEY);
        if (store.isCached(CACHE_KEY) && (cached.length > 0 || store.isAllLoaded(CACHE_KEY))) {
            setRows(cached);
            setAllLoaded(store.isAllLoaded(CACHE_KEY));
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${BASE_URL}/api/enterprise-support?page=0&size=100`, { headers: authHeaders });
            const data = await safeJson(res);
            const list = Array.isArray(data) ? data : [];
            setRows(list);
            store.setRows(CACHE_KEY, list, false);
            setAllLoaded(false);
        } catch {
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllRows = async () => {
        setLoadingAll(true);
        try {
            const res = await fetch(`${BASE_URL}/api/enterprise-support`, { headers: authHeaders });
            const data = await safeJson(res);
            const list = Array.isArray(data) ? data : [];
            setRows(list);
            store.markAllLoaded(CACHE_KEY, list);
            setAllLoaded(true);
            setVisibleCount(list.length);
        } catch {
            flash("err", "Failed to load all records");
        } finally {
            setLoadingAll(false);
        }
    };

    useEffect(() => { fetchRows(); }, []);
    useEffect(() => { setVisibleCount(100); }, [search]);

    /* ================= SEARCH ================= */
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) =>
            r.beneficiaryId?.toLowerCase().includes(q) ||
            r.sector?.toLowerCase().includes(q) ||
            r.setupType?.toLowerCase().includes(q) ||
            r.udyamRegistrationNumber?.toLowerCase().includes(q) ||
            r.gstNumber?.toLowerCase().includes(q)
        );
    }, [rows, search]);

    /* ================= UPLOAD ================= */
    const CHUNK_SIZE = 500;
    const PARALLEL = 2;

    const uploadExcel = async () => {
        if (!file) return flash("err", "Select Excel file");
        let headerRow, dataRows;
        try {
            const buffer = await file.arrayBuffer();
            const wb = XLSX.read(buffer, { type: "array" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
            headerRow = raw[0];
            dataRows = raw.slice(1);
        } catch (err) {
            return flash("err", `Cannot read file: ${err.message}`);
        }
        if (!dataRows.length) return flash("err", "Excel file is empty");

        const totalRows = dataRows.length;
        const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);
        setUploading(true);
        setUploadProgress({ uploaded: 0, total: totalRows, chunk: 0, totalChunks });

        let uploadedSoFar = 0, totalInserted = 0, totalUpdated = 0, totalSkipped = 0;
        let failed = false, firstError = "";

        const uploadOneChunk = async (i) => {
            const chunkRows = [headerRow, ...dataRows.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE)];
            const res = await fetch(`${BASE_URL}/api/enterprise-support/upload-json`, {
                method: "POST",
                headers: { ...authHeaders, "Content-Type": "application/json" },
                body: JSON.stringify(chunkRows),
            });
            if (res.status === 413) throw new Error(`Chunk ${i + 1}: Payload too large (413)`);
            const text = await res.text();
            let data;
            try { data = JSON.parse(text); } catch { data = text; }
            if (!res.ok) throw new Error(`Chunk ${i + 1}: ${data?.message || `HTTP ${res.status}`}`);
            return { inserted: data?.inserted ?? 0, updated: data?.updated ?? 0, skipped: data?.skipped ?? 0 };
        };

        for (let batchStart = 0; batchStart < totalChunks && !failed; batchStart += PARALLEL) {
            const batchEnd = Math.min(batchStart + PARALLEL, totalChunks);
            const indices = Array.from({ length: batchEnd - batchStart }, (_, k) => batchStart + k);
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks });
            const results = await Promise.allSettled(indices.map(uploadOneChunk));
            for (const result of results) {
                if (result.status === "fulfilled") {
                    totalInserted += result.value.inserted;
                    totalUpdated  += result.value.updated;
                    totalSkipped  += result.value.skipped;
                    uploadedSoFar += result.value.inserted + result.value.updated + result.value.skipped;
                } else {
                    failed = true;
                    firstError = result.reason?.message || "Upload failed";
                }
            }
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks, error: failed });
        }

        setUploading(false);
        if (!failed) {
            flash("ok", `Upload complete — Inserted: ${totalInserted.toLocaleString()}, Updated: ${totalUpdated.toLocaleString()}, Skipped: ${totalSkipped.toLocaleString()}`);
            setFile(null);
            setUploadProgress(null);
            store.invalidate(CACHE_KEY);
            fetchRows();
        } else {
            flash("err", firstError);
            setUploadProgress(prev => prev ? { ...prev, error: true } : null);
        }
    };

    const downloadTemplate = () => {
        const ws = XLSX.utils.aoa_to_sheet([
            TEMPLATE_HEADERS,
            ["EP/BEN-705676", "Textile", "Hybrid", "Yes", "UDYAM-DL-00-0000001", "Yes", "Yes", "Yes", "Yes", "07TESTGSTIN0001X1Z0",
             "WhatsApp Catalogue", "https://wa.me/c/91XXXXXXXXXX", "Instagram", "https://instagram.com/example",
             "", "", "", "", "", "", "Retail", "Sample Business"],
        ]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Template");
        XLSX.writeFile(wb, "edp_business_setup_template.xlsx");
    };

    /* ================= EXPORT ================= */
    const [exporting, setExporting] = useState(false);

    const exportExcel = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            let data = rows;
            if (!allLoaded) {
                const res = await fetch(`${BASE_URL}/api/enterprise-support`, { headers: authHeaders });
                const json = await safeJson(res);
                data = Array.isArray(json) ? json : [];
                setRows(data);
                store.markAllLoaded(CACHE_KEY, data);
                setAllLoaded(true);
                setVisibleCount(data.length);
            }
            if (!data.length) return flash("err", "No data to export");
            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "EDPBusinessSetup");
            XLSX.writeFile(wb, "edp_business_setup.xlsx");
            flash("ok", `Exported ${data.length.toLocaleString()} records`);
        } catch (e) {
            flash("err", "Export failed: " + e.message);
        } finally {
            setExporting(false);
        }
    };

    /* ================= EDIT ================= */
    const openEdit = (row) => {
        setEditingId(row.id);
        setForm({ ...emptyForm, ...row });
    };

    const saveEdit = async () => {
        setSaving(true);
        try {
            const res = await fetch(`${BASE_URL}/api/enterprise-support/${editingId}`, {
                method: "PUT",
                headers: jsonHeaders,
                body: JSON.stringify(form),
            });
            if (res.ok) {
                flash("ok", "Record updated");
                setEditingId(null);
                store.invalidate(CACHE_KEY);
                fetchRows();
            } else {
                const data = await safeJson(res);
                flash("err", data?.message || "Update failed");
            }
        } catch {
            flash("err", "Update failed");
        } finally {
            setSaving(false);
        }
    };

    /* ================= DELETE ================= */
    const deleteRow = async (id) => {
        if (!window.confirm("Delete this record? This cannot be undone.")) return;
        try {
            const res = await fetch(`${BASE_URL}/api/enterprise-support/${id}`, {
                method: "DELETE",
                headers: authHeaders,
            });
            if (res.ok) {
                flash("ok", "Record deleted");
                store.invalidate(CACHE_KEY);
                fetchRows();
            } else {
                flash("err", "Delete failed");
            }
        } catch {
            flash("err", "Delete failed");
        }
    };

    /* ================= FIELD HELPERS ================= */
    const Field = ({ label, fieldKey }) => (
        <div className="field">
            <label>{label}</label>
            <input
                type="text"
                value={form[fieldKey] ?? ""}
                onChange={(e) => setForm((p) => ({ ...p, [fieldKey]: e.target.value }))}
            />
        </div>
    );

    const SelectField = ({ label, fieldKey, options }) => (
        <div className="field">
            <label>{label}</label>
            <select
                value={form[fieldKey] ?? ""}
                onChange={(e) => setForm((p) => ({ ...p, [fieldKey]: e.target.value }))}
            >
                <option value="">— select —</option>
                {options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
        </div>
    );

    /* ================= UI ================= */
    return (
        <div className="cmp-page">
            <div className="header">
                <h1>EDP Business Setup</h1>
            </div>

            {msg.text && (
                <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>{msg.text}</span>
                    <button onClick={() => setMsg({ type: "", text: "" })}
                        style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
                </div>
            )}

            {uploadProgress && (
                <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "14px 18px", marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: "#1e3a8a" }}>
                            {uploadProgress.error
                                ? `⚠ Stopped at chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}`
                                : uploading
                                    ? `Uploading chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}…`
                                    : `Upload complete`}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
                            {uploadProgress.uploaded.toLocaleString()} / {uploadProgress.total.toLocaleString()} rows
                            &nbsp;({Math.round((uploadProgress.uploaded / uploadProgress.total) * 100)}%)
                        </span>
                    </div>
                    <div style={{ height: 10, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
                        <div style={{
                            height: "100%",
                            width: `${(uploadProgress.uploaded / uploadProgress.total) * 100}%`,
                            background: uploadProgress.error ? "#ef4444" : "linear-gradient(90deg,#1e40af,#3b82f6)",
                            borderRadius: 999,
                            transition: "width 0.3s ease",
                        }} />
                    </div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
                        {uploadProgress.total.toLocaleString()} total rows · {uploadProgress.totalChunks} chunk{uploadProgress.totalChunks > 1 ? "s" : ""} of {CHUNK_SIZE} rows each
                    </div>
                </div>
            )}

            {/* Upload */}
            <div className="card grid-3">
                <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setFile(e.target.files[0] || null)}
                    style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 12px", background: "#fff" }}
                />
                <button className="btn btn-primary" onClick={uploadExcel} disabled={uploading}>
                    {uploading ? `Uploading ${uploadProgress?.chunk ?? ""}/${uploadProgress?.totalChunks ?? ""}…` : "Upload Excel"}
                </button>
                <button className="btn btn-outline" onClick={downloadTemplate}>
                    Download Template
                </button>
            </div>

            {/* Table */}
            <div className="card">
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <input
                        className="search"
                        placeholder="Search beneficiary ID, sector, GST number, Udyam no..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ flex: "0 0 70%", marginBottom: 0 }}
                    />
                    <button
                        className="btn btn-outline"
                        onClick={exportExcel}
                        style={{ flex: "0 0 calc(30% - 8px)", fontSize: "0.8rem", padding: "6px 10px" }}
                        disabled={exporting}
                    >
                        {exporting ? "Processing..." : "Export All Data"}
                    </button>
                </div>

                <div className="table-wrap">
                    <table className="ep-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                {COLUMNS.map((c) => <th key={c.key}>{c.label}</th>)}
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.slice(0, visibleCount).map((r, i) => (
                                <tr key={r.id}>
                                    <td>{i + 1}</td>
                                    {COLUMNS.map((c) => (
                                        <td key={c.key}>
                                            {c.key.endsWith("Link") && r[c.key]
                                                ? <a href={r[c.key]} target="_blank" rel="noreferrer"
                                                    style={{ color: "#2563eb", textDecoration: "underline", maxWidth: 160, display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", verticalAlign: "bottom" }}>
                                                    {r[c.key]}
                                                </a>
                                                : r[c.key] ?? ""}
                                        </td>
                                    ))}
                                    <td className="actions">
                                        <button className="btn btn-sm" onClick={() => openEdit(r)}>Edit</button>
                                        <button className="btn btn-sm btn-danger" onClick={() => deleteRow(r.id)}>Delete</button>
                                    </td>
                                </tr>
                            ))}

                            {!filtered.length && (
                                <tr>
                                    <td colSpan={COLUMNS.length + 2} className="muted">
                                        {loading ? "Loading..." : "No records found"}
                                    </td>
                                </tr>
                            )}

                            {!allLoaded && !loading && (
                                <tr>
                                    <td colSpan={COLUMNS.length + 2} style={{ textAlign: "center", padding: "10px" }}>
                                        <button className="btn btn-outline" onClick={fetchAllRows} disabled={loadingAll}>
                                            {loadingAll ? "Loading all data…" : "Load All Data"}
                                        </button>
                                    </td>
                                </tr>
                            )}

                            {filtered.length > visibleCount && (
                                <tr>
                                    <td colSpan={COLUMNS.length + 2} style={{ textAlign: "center", padding: "10px" }}>
                                        <button className="btn btn-sm" onClick={() => setVisibleCount((c) => c + 100)}>
                                            Load More ({filtered.length - visibleCount} remaining)
                                        </button>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* EDIT MODAL */}
            {editingId !== null && createPortal(
                <div className="modal-overlay" onClick={() => setEditingId(null)}>
                    <div className="ep-modal wide" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-title">Edit EDP Business Setup Record</div>

                        <div className="modal-grid">
                            <Field label="Beneficiary ID *" fieldKey="beneficiaryId" />
                            <Field label="Sector" fieldKey="sector" />
                            <Field label="Setup Type" fieldKey="setupType" />
                            <SelectField label="Udyam Registration" fieldKey="udyamRegistration" options={["Yes", "No"]} />
                            <Field label="Udyam Registration Number" fieldKey="udyamRegistrationNumber" />
                            <SelectField label="Logbooks Created" fieldKey="logbooksCreated" options={["Yes", "No"]} />
                            <SelectField label="Digital Payments Setup" fieldKey="digitalPaymentsSetup" options={["Yes", "No"]} />
                            <SelectField label="Branding Provided" fieldKey="brandingProvided" options={["Yes", "No"]} />
                            <SelectField label="GST (Business Registered)" fieldKey="gstRegistered" options={["Yes", "No"]} />
                            <Field label="GST Number" fieldKey="gstNumber" />
                            <Field label="Platform 1 Listing" fieldKey="platform1Listing" />
                            <Field label="Platform 1 Link" fieldKey="platform1Link" />
                            <Field label="Platform 2 Listing" fieldKey="platform2Listing" />
                            <Field label="Platform 2 Link" fieldKey="platform2Link" />
                            <Field label="Platform 3 Listing" fieldKey="platform3Listing" />
                            <Field label="Platform 3 Link" fieldKey="platform3Link" />
                            <Field label="Platform 4 Listing" fieldKey="platform4Listing" />
                            <Field label="Platform 4 Link" fieldKey="platform4Link" />
                            <Field label="Platform 5 Listing" fieldKey="platform5Listing" />
                            <Field label="Platform 5 Link" fieldKey="platform5Link" />
                            <Field label="Trade" fieldKey="trade" />
                            <Field label="Business Name" fieldKey="businessName" />
                        </div>

                        <div className="modal-actions">
                            <button className="btn" onClick={() => setEditingId(null)}>Cancel</button>
                            <button className="btn btn-primary" onClick={saveEdit} disabled={saving}>
                                {saving ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            <style>{`
.cmp-page { padding: 22px; background: #f7f7fb; min-height: 100vh; font-family: Inter; }
.header h1 { margin: 0 0 14px; font-size: 26px; font-weight: 900; }

.flash { padding: 10px; border-radius: 10px; margin: 12px 0; font-weight: 700; }
.flash-ok  { background: #ecfdf5; color: #065f46; }
.flash-err { background: #fef2f2; color: #991b1b; }

.card { background: #fff; border: 1px solid #e5e7eb; border-radius: 14px; padding: 16px; margin-bottom: 14px; }
.grid-3 { display: grid; grid-template-columns: 1fr auto auto; gap: 10px; align-items: center; }

.btn { border: 1px solid #e5e7eb; background: #fff; padding: 10px 14px; border-radius: 12px; font-weight: 800; cursor: pointer; font-size: 13px; }
.btn-primary { background: #2563eb; color: #fff; border-color: #2563eb; }
.btn-outline { background: #fff; }
.btn-sm { padding: 6px 10px; font-size: 12px; }
.btn-danger { border-color: #fca5a5; color: #991b1b; }
.btn:disabled { opacity: .6; cursor: not-allowed; }

.search { padding: 10px; border-radius: 12px; border: 1px solid #e5e7eb; width: 100%; box-sizing: border-box; }
.table-wrap { overflow-x: auto; }
.ep-table { width: 100%; border-collapse: collapse; }
.ep-table th, .ep-table td { padding: 10px; border-bottom: 1px solid #e5e7eb; font-size: 13px; white-space: nowrap; text-align: left; }
.ep-table th { color: #6b7280; }
.actions { display: flex; gap: 8px; }
.muted { color: #6b7280; font-weight: 700; text-align: center; }

.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.45); display: flex; align-items: center; justify-content: center; z-index: 99999; }
.ep-modal { background: #fff; padding: 24px; border-radius: 16px; width: 700px; max-height: 90vh; overflow-y: auto; }
.ep-modal.wide { width: 820px; }
.modal-title { font-size: 18px; font-weight: 900; margin-bottom: 16px; }
.modal-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0 16px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 16px; }

.field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 10px; }
.field label { font-size: 12px; color: #6b7280; font-weight: 800; }
.field input, .field select { padding: 10px; border-radius: 12px; border: 1px solid #e5e7eb; font-size: 13px; }
            `}</style>
        </div>
    );
}
